import {
  ANALYTICS_ALL_EVENTS,
  ANALYTICS_DIMENSION_NAMES,
  ANALYTICS_EVENT_NAME_PATTERN,
  ANALYTICS_EVENT_NAMES,
  ANALYTICS_EVENTS,
  ANALYTICS_SERIES_MAX_EVENTS,
  ANALYTICS_SOURCES,
  type AnalyticsDimension,
  type AnalyticsMode,
  type AnalyticsSource,
  analyticsEventLabel,
  BROWSER_ANALYTICS_EVENT_NAMES,
} from "@loot-os/api-contract";

import { type AnalyticsWindow, isAnalyticsWindow, windowBucketCount } from "./timeBuckets";

/**
 * The Insights page's vocabulary and URL state, shared with the router's `validateSearch`. An
 * insight is up to five events charted over one window from the rollups, or one event broken
 * down by one dimension (`analyticsBreakdown.ts`), and the URL is the whole of it, so an insight
 * is a link.
 *
 * One rule runs through this module: distinct shoppers are never added up, across buckets or
 * across events. It decides which charts a selection may be drawn as (`chartRefusal`) and what
 * the table may show (`InsightTableRow`), and it is applied where the URL is parsed, so a chart
 * that would double-count is never rendered rather than merely never offered.
 */

export const INSIGHT_MEASURES = ["events", "users"] as const;
export type InsightMeasure = (typeof INSIGHT_MEASURES)[number];

export const INSIGHT_MEASURE_LABELS: Record<InsightMeasure, string> = {
  events: "Events",
  users: "Shoppers",
};

export const INSIGHT_CHARTS = ["line", "bar", "stacked"] as const;
export type InsightChart = (typeof INSIGHT_CHARTS)[number];

export const INSIGHT_CHART_LABELS: Record<InsightChart, string> = {
  line: "Lines",
  bar: "Bars",
  stacked: "Stacked bars",
};

const DEFAULT_WINDOW: AnalyticsWindow = "7d";
const DEFAULT_MEASURE: InsightMeasure = "events";
const DEFAULT_CHART: InsightChart = "line";
const DEFAULT_EVENT_NAMES: readonly string[] = [ANALYTICS_ALL_EVENTS];

function isInsightMeasure(value: unknown): value is InsightMeasure {
  return typeof value === "string" && (INSIGHT_MEASURES as readonly string[]).includes(value);
}

function isInsightChart(value: unknown): value is InsightChart {
  return typeof value === "string" && (INSIGHT_CHARTS as readonly string[]).includes(value);
}

function isAnalyticsDimension(value: unknown): value is AnalyticsDimension {
  return (
    typeof value === "string" && (ANALYTICS_DIMENSION_NAMES as readonly string[]).includes(value)
  );
}

/**
 * The most series a breakdown charts, one per `--chart-N` color. Known before any row is read,
 * so whether bars are legible is decided from the URL alone and the parser stays idempotent.
 */
export const BREAKDOWN_SERIES_MAX = ANALYTICS_SERIES_MAX_EVENTS;

/** What the page reads from the URL; absent means the default. */
export interface InsightsSearch {
  w?: AnalyticsWindow;
  m?: InsightMeasure;
  c?: InsightChart;
  /** The events, comma-joined in series order; a string, because the router JSON-encodes arrays. */
  e?: string;
  /** The dimension the one event is broken down by; absent means no breakdown. */
  b?: AnalyticsDimension;
}

/** A search with every key written; `b` alone may be absent, which is "no breakdown". */
export type InsightLinkSearch = Required<Omit<InsightsSearch, "b">> & Pick<InsightsSearch, "b">;

/**
 * An insight with its defaults applied. Shoppers have no stacked chart in the type, so code
 * that draws one cannot be written.
 */
export type Insight = {
  window: AnalyticsWindow;
  eventNames: string[];
  /** Set, the insight charts `eventNames[0]` alone, one series per value of the dimension. */
  breakdown: AnalyticsDimension | null;
} & (
  | { measure: "events"; chart: InsightChart }
  | { measure: "users"; chart: Exclude<InsightChart, "stacked"> }
);

/**
 * The event names a URL value carries, in order: each the all-events name or a name by pattern
 * (not the catalog, since a rollup row outlives its catalog entry), once each, at most five.
 * Anything else, including a value the router decoded to a non-string, is dropped.
 */
export function parseEventNames(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const names = value
    .split(",")
    .filter((name) => name === ANALYTICS_ALL_EVENTS || ANALYTICS_EVENT_NAME_PATTERN.test(name));
  return [...new Set(names)].slice(0, ANALYTICS_SERIES_MAX_EVENTS);
}

export function joinEventNames(eventNames: readonly string[]): string {
  return eventNames.join(",");
}

/** What decides which charts a selection may be drawn as. */
export interface InsightSelection {
  window: AnalyticsWindow;
  measure: InsightMeasure;
  eventNames: readonly string[];
  breakdown?: AnalyticsDimension | null;
}

/**
 * The most bars one chart draws side by side: past this each is a hairline nobody can read (five
 * events over 90 days would be 450). A day of hours holds five events, a month of days four.
 */
export const SIDE_BY_SIDE_BARS_MAX = 120;

/**
 * Why a selection cannot be drawn as a chart. The stacked ones are correctness, a stack that
 * would count something twice; `bars_too_thin` is legibility.
 */
export const CHART_REFUSALS = [
  "shoppers_stacked",
  "breakdown_shoppers_stacked",
  "all_events_stacked",
  "bars_too_thin",
] as const;
export type ChartRefusal = (typeof CHART_REFUSALS)[number];

export const CHART_REFUSAL_MESSAGES: Record<ChartRefusal, string> = {
  shoppers_stacked:
    "Shoppers cannot be stacked: a shopper who did two of these events would be counted twice.",
  breakdown_shoppers_stacked:
    "Shoppers cannot be stacked across a breakdown: a shopper seen under two values, a phone and a laptop, would be counted twice.",
  all_events_stacked:
    "All events cannot be stacked with other events: its count already contains theirs.",
  bars_too_thin:
    "Bars side by side would be too thin to read for this many series over this window. Lines carry them, as do fewer events or a shorter window.",
};

/** The one reason a selection cannot be drawn as `chart`, or null when it can. Lines always can. */
export function chartRefusal(
  chart: InsightChart,
  selection: InsightSelection,
): ChartRefusal | null {
  const { measure, eventNames, window, breakdown } = selection;
  if (chart === "stacked") {
    if (measure === "users") return breakdown ? "breakdown_shoppers_stacked" : "shoppers_stacked";
    // A breakdown's values partition the one event's count, so its events always stack.
    if (!breakdown && eventNames.length > 1 && eventNames.includes(ANALYTICS_ALL_EVENTS)) {
      return "all_events_stacked";
    }
    return null;
  }
  const seriesCount = breakdown ? BREAKDOWN_SERIES_MAX : eventNames.length;
  if (
    chart === "bar" &&
    seriesCount > 1 &&
    seriesCount * windowBucketCount(window) > SIDE_BY_SIDE_BARS_MAX
  ) {
    return "bars_too_thin";
  }
  return null;
}

/**
 * The chart to draw for a selection: the one asked for, or the next one down that it may be
 * drawn as. Stacked falls back to bars side by side, and those to lines.
 */
export function legalInsightChart(chart: InsightChart, selection: InsightSelection): InsightChart {
  if (chart === "stacked" && chartRefusal("stacked", selection)) {
    return legalInsightChart("bar", selection);
  }
  if (chart === "bar" && chartRefusal("bar", selection)) return "line";
  return chart;
}

/**
 * The route's `validateSearch`: anything malformed is dropped rather than refused, and a chart
 * the window, measure and events beside it (or their defaults) cannot be drawn as becomes the
 * next one down that they can. A breakdown charts one event, so with `b` the events are cut to
 * the first. The router writes this output back to the address bar, so it is idempotent.
 */
export function parseInsightsSearch(search: Record<string, unknown>): InsightsSearch {
  const w = isAnalyticsWindow(search.w) ? search.w : undefined;
  const m = isInsightMeasure(search.m) ? search.m : undefined;
  const b = isAnalyticsDimension(search.b) ? search.b : undefined;
  const eventNames = brokenDownEventNames(parseEventNames(search.e), b);
  return {
    w,
    m,
    c: isInsightChart(search.c)
      ? legalInsightChart(search.c, {
          window: w ?? DEFAULT_WINDOW,
          measure: m ?? DEFAULT_MEASURE,
          eventNames: eventNames.length > 0 ? eventNames : DEFAULT_EVENT_NAMES,
          breakdown: b,
        })
      : undefined,
    e: eventNames.length > 0 ? joinEventNames(eventNames) : undefined,
    ...(b ? { b } : {}),
  };
}

/** The events a selection charts: all of them, or under a breakdown the first alone. */
function brokenDownEventNames(
  eventNames: string[],
  breakdown: AnalyticsDimension | null | undefined,
): string[] {
  return breakdown ? eventNames.slice(0, 1) : eventNames;
}

/** A search with its defaults applied; legal whatever the search held, parsed or built by code. */
export function resolveInsight(search: InsightsSearch): Insight {
  const window = search.w ?? DEFAULT_WINDOW;
  const measure = search.m ?? DEFAULT_MEASURE;
  const breakdown = isAnalyticsDimension(search.b) ? search.b : null;
  const parsed = brokenDownEventNames(parseEventNames(search.e), breakdown);
  const eventNames = parsed.length > 0 ? parsed : [...DEFAULT_EVENT_NAMES];
  const chart = legalInsightChart(search.c ?? DEFAULT_CHART, {
    window,
    measure,
    eventNames,
    breakdown,
  });
  if (measure === "users") {
    // `legalInsightChart` never answers stacked for shoppers; this narrows the type to say so.
    return { window, measure, chart: chart === "stacked" ? "bar" : chart, eventNames, breakdown };
  }
  return { window, measure, chart, eventNames, breakdown };
}

/**
 * The search that reopens an insight. Every key is written, defaults included, so a shared
 * link keeps its meaning if a default changes.
 */
export function insightSearch(insight: Insight): InsightLinkSearch {
  return {
    w: insight.window,
    m: insight.measure,
    c: insight.chart,
    e: joinEventNames(insight.eventNames),
    ...(insight.breakdown ? { b: insight.breakdown } : {}),
  };
}

/**
 * The selection after one event is clicked in the picker: removed when present (the last one
 * removed leaves All events, since an insight always charts something), added at the end when
 * there is room, unchanged at the cap.
 */
export function toggleEventName(eventNames: readonly string[], name: string): string[] {
  if (eventNames.includes(name)) {
    const rest = eventNames.filter((other) => other !== name);
    return rest.length > 0 ? rest : [...DEFAULT_EVENT_NAMES];
  }
  if (eventNames.length >= ANALYTICS_SERIES_MAX_EVENTS) return [...eventNames];
  return [...eventNames, name];
}

/** Starting points, each a plain link. */
export const INSIGHT_PRESETS: readonly { label: string; search: InsightLinkSearch }[] = [
  {
    label: "Box opens",
    search: {
      w: "7d",
      m: "events",
      c: "line",
      e: "page_view,open_clicked,box_opened,reveal_claimed",
    },
  },
  {
    label: "Battles",
    search: {
      w: "7d",
      m: "events",
      c: "line",
      e: "battle_created,battle_joined,battle_completed,battle_claimed",
    },
  },
  {
    label: "Checkout",
    search: {
      w: "7d",
      m: "events",
      c: "line",
      e: "checkout_started,checkout_returned,balance_purchase_completed",
    },
  },
  {
    label: "Opens by segment",
    search: { w: "7d", m: "events", c: "stacked", e: "box_opened", b: "segment" },
  },
  {
    label: "Page views by country",
    search: { w: "7d", m: "events", c: "stacked", e: "page_view", b: "country" },
  },
];

const SOURCE_HEADINGS: Record<AnalyticsSource, string> = {
  browser: "Browser events",
  server: "Server events",
};

/** The catalog as the picker lists it: what shoppers did, then what happened. */
export const INSIGHT_EVENT_GROUPS = ANALYTICS_SOURCES.map((source) => ({
  source,
  heading: SOURCE_HEADINGS[source],
  eventNames: ANALYTICS_EVENT_NAMES.filter((name) => ANALYTICS_EVENTS[name].source === source),
}));

export function insightEventLabel(eventName: string): string {
  return eventName === ANALYTICS_ALL_EVENTS ? "All events" : analyticsEventLabel(eventName);
}

/** One charted event. */
export interface InsightSeries {
  /**
   * The key its values sit under in a chart row, by position (`s0`…`s4`), never the event
   * name: the chart writes a `--color-<key>` CSS variable per key, which `*` cannot be part of,
   * and a name typed into the URL could otherwise collide with the row's own `bucket`.
   */
  key: string;
  eventName: string;
  label: string;
  color: string;
}

/** The series for a selection, in its order, colored by position. */
export function insightSeries(eventNames: readonly string[]): InsightSeries[] {
  return eventNames.map((eventName, i) => ({
    key: `s${i}`,
    eventName,
    label: insightEventLabel(eventName),
    color: `var(--chart-${i + 1})`,
  }));
}

/** A rollup as `analytics.seriesByEvent` answers it. */
export interface SeriesByEventRow {
  bucket: string;
  eventName: string;
  events: number;
  users: number;
}

/** One bucket of the chart: its start under `bucket`, and one value per series key. */
export type InsightChartRow = Record<string, string | number>;

/**
 * The chart's rows: every bucket of the window, chronological, with each series' value or zero
 * (the rollups hold only non-empty buckets, so a missing row is a zero, not a gap). Rows of
 * events outside the series are ignored, which is what a previous selection's data is while
 * the next loads.
 */
export function pivotSeries(args: {
  rows: readonly SeriesByEventRow[];
  series: readonly InsightSeries[];
  measure: InsightMeasure;
  buckets: readonly Date[];
}): InsightChartRow[] {
  const values = new Map(
    args.rows.map((row) => [`${row.bucket}|${row.eventName}`, row[args.measure]]),
  );
  return args.buckets.map((bucket) => {
    const iso = bucket.toISOString();
    const chartRow: InsightChartRow = { bucket: iso };
    for (const s of args.series) chartRow[s.key] = values.get(`${iso}|${s.eventName}`) ?? 0;
    return chartRow;
  });
}

interface InsightTableRowBase extends InsightSeries {
  /** Mean per bucket over the buckets with data, rounded, as the Analytics page's cards read it. */
  avgPerBucket: number;
  /** The busiest bucket. */
  peakPerBucket: number;
}

/**
 * One series over the window. Events have a total; shoppers do not, because distinct shoppers
 * per bucket cannot be added into a count of shoppers, so the field does not exist to be shown.
 */
export type InsightTableRow =
  | (InsightTableRowBase & { measure: "events"; total: number })
  | (InsightTableRowBase & { measure: "users" });

/** The table under the chart, one row per series, from the same rows the chart draws. */
export function insightTableRows(args: {
  rows: readonly SeriesByEventRow[];
  series: readonly InsightSeries[];
  measure: InsightMeasure;
}): InsightTableRow[] {
  return args.series.map((s) => {
    const values = args.rows
      .filter((row) => row.eventName === s.eventName)
      .map((row) => row[args.measure]);
    const sum = values.reduce((total, value) => total + value, 0);
    const base = {
      ...s,
      avgPerBucket: values.length > 0 ? Math.round(sum / values.length) : 0,
      peakPerBucket: Math.max(0, ...values),
    };
    return args.measure === "events"
      ? { ...base, measure: "events", total: sum }
      : { ...base, measure: "users" };
  });
}

function countsBrowserEvents(eventName: string): boolean {
  return (
    eventName === ANALYTICS_ALL_EVENTS ||
    (BROWSER_ANALYTICS_EVENT_NAMES as readonly string[]).includes(eventName)
  );
}

/**
 * Why a browser event's series may be flat without anything being wrong, for the environment's
 * analytics mode; null when the mode records every browser event, the mode is not known yet, or
 * no charted series counts browser events.
 */
export function browserEventsHint(
  mode: AnalyticsMode | undefined,
  eventNames: readonly string[],
): string | null {
  if (mode === undefined || mode === "enabled") return null;
  if (!eventNames.some(countsBrowserEvents)) return null;
  return mode === "disabled"
    ? "This environment's analytics mode is Disabled, so browser events are not recorded and their series stay at zero. Server events are recorded in every mode."
    : "This environment's analytics mode is Consent required: browser events are recorded only after a shopper accepts analytics cookies, and a storefront with no cookie banner records none, so their series can be flat. Server events are recorded in every mode.";
}
